import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../paidClients.css";
import { exportClients, getClients, SERVER_SORTS } from "../api/paidClientsService";
import PaidClientsTable, { SORT_GETTERS } from "../components/PaidClientsTable";
import ClientDrawer from "../components/ClientDrawer";
import { FLAGS, STAGES, TARIFFS } from "../utils/constants";
import { fmtDate, fmtDateTime, fmtMoney, fmtNum, fmtPct } from "../utils/format";

// Флаги, которых нет на сервере, — фильтруем загруженный список
const LOCAL_FLAGS = {
  no_bin: (c) => !c.firstBin,
  no_amo: (c) => c.inAmoCrm !== true,
};

const DEFAULT_SORT = "daysLeft";
const DEFAULT_DIR = "asc";
const STALE_MS = 24 * 60 * 60 * 1000;

const MONTHS_IN = [
  "январе",
  "феврале",
  "марте",
  "апреле",
  "мае",
  "июне",
  "июле",
  "августе",
  "сентябре",
  "октябре",
  "ноябре",
  "декабре",
];

function Stat({ value, label, hot, on, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`pc-stat ${onClick ? "click" : ""} ${hot ? "hot" : ""} ${on ? "on" : ""}`}
      aria-pressed={onClick ? !!on : undefined}
      onClick={onClick}
    >
      <b>{value}</b>
      <span>{label}</span>
    </Tag>
  );
}

function Toast({ text, onDone }) {
  useEffect(() => {
    if (!text) return;
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [text, onDone]);
  return (
    <div className={`pc-toast ${text ? "on" : ""}`} role="status">
      {text}
    </div>
  );
}

const PaidClientsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") || "";
  const tariff = searchParams.get("tariff") || "";
  const stage = searchParams.get("stage") || "";
  const flagsRaw = searchParams.get("flags") || "";
  const flags = useMemo(() => (flagsRaw ? flagsRaw.split(",") : []), [flagsRaw]);
  const includeInternal = searchParams.get("includeInternal") === "true";
  const sort = searchParams.get("sort") || DEFAULT_SORT;
  const dir = searchParams.get("dir") || DEFAULT_DIR;
  const clientId = searchParams.get("client");
  const tab = searchParams.get("tab") || "passport";

  const [searchText, setSearchText] = useState(q);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState("");
  const clearToast = useCallback(() => setToast(""), []);

  const updateParams = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined || v === "" || v === false) next.delete(k);
            else next.set(k, String(v));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // debounce поиска 300 мс
  useEffect(() => {
    if (searchText === q) return;
    const t = setTimeout(() => updateParams({ q: searchText }), 300);
    return () => clearTimeout(t);
  }, [searchText, q, updateParams]);

  const query = useMemo(
    () => ({ q, tariff, stage, flags, includeInternal, sort, dir }),
    [q, tariff, stage, flags, includeInternal, sort, dir],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getClients(query);
      setItems(res.items);
      setSummary(res.summary);
    } catch (e) {
      console.error(e);
      setError(e.message || "Не удалось загрузить клиентов");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  // Сортирует и фильтрует сервер; на фронте — только то, чего сервер не умеет
  const visibleItems = useMemo(() => {
    const localFilters = flags.map((f) => LOCAL_FLAGS[f]).filter(Boolean);
    let result = localFilters.length
      ? items.filter((c) => localFilters.every((fn) => fn(c)))
      : items;

    const getter = SORT_GETTERS[sort];
    if (!SERVER_SORTS.includes(sort) && getter) {
      const k = dir === "desc" ? -1 : 1;
      result = [...result].sort((a, b) => {
        const va = getter(a);
        const vb = getter(b);
        if (va < vb) return -1 * k;
        if (va > vb) return 1 * k;
        return 0;
      });
    }
    return result;
  }, [items, flags, sort, dir]);

  const s = summary || {};
  const goszakupDataAt = s.goszakupDataAt;

  const toggleFlag = (key) => {
    const next = flags.includes(key) ? flags.filter((f) => f !== key) : [...flags, key];
    updateParams({ flags: next.join(",") });
  };

  const handleSort = (key) => {
    if (sort === key) updateParams({ dir: dir === "asc" ? "desc" : "asc" });
    else updateParams({ sort: key, dir: "asc" });
  };

  const openClient = (userId, nextTab) =>
    updateParams({ client: userId, tab: nextTab || tab || "passport" });
  const closeClient = () => updateParams({ client: null, tab: null });

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportClients(query);
    } catch (e) {
      setToast(e.message);
    } finally {
      setExporting(false);
    }
  };

  const isStale =
    summary &&
    (!goszakupDataAt || Date.now() - new Date(goszakupDataAt).getTime() > STALE_MS);

  return (
    <div className="pc-root">
      <div className="pc-wrap">
      <header className="pc-head">
        <div className="pc-brand">
          <div className="mark" aria-hidden="true" />
          <div>
            <h1>Платные клиенты</h1>
            <div className="sub">Админка Colibri · данные на {fmtDate(new Date())}</div>
          </div>
        </div>
        {isStale && (
          <div className="pc-note">
            {goszakupDataAt
              ? `Данные госзакупок на ${fmtDateTime(goszakupDataAt)}`
              : "Данные госзакупок ещё не рассчитаны"}
          </div>
        )}
      </header>

      <section className="pc-band" aria-label="Сводка">
        <Stat value={fmtNum(s.paidClients)} label="платных клиентов" />
        <Stat value={fmtMoney(s.mrr)} label="выручка в месяц" />
        <Stat
          value={fmtNum(s.expiring7d)}
          label="истекают за 7 дней"
          hot={s.expiring7d > 0}
          on={flags.includes("expiring")}
          onClick={() => toggleFlag("expiring")}
        />
        <Stat
          value={fmtPct(s.renewedPctMonth)}
          label={`продлили в ${MONTHS_IN[new Date().getMonth()]}`}
          on={flags.includes("expired")}
          onClick={() => toggleFlag("expired")}
        />
        <Stat
          value={fmtNum(s.silent)}
          label="молчат 7+ дней"
          hot={s.silent > 0}
          on={flags.includes("silent")}
          onClick={() => toggleFlag("silent")}
        />
        <Stat value={fmtPct(s.appliedPct)} label="подали заявку за подписку" />
      </section>

      <div className="pc-filters">
        <input
          type="search"
          className="pc-input search browser-default"
          placeholder="Поиск: имя, телефон, БИН"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <select
          className="pc-select"
          value={tariff}
          onChange={(e) => updateParams({ tariff: e.target.value })}
        >
          <option value="">Все тарифы</option>
          {TARIFFS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="pc-select"
          value={stage}
          onChange={(e) => updateParams({ stage: e.target.value })}
        >
          <option value="">Все этапы</option>
          {Object.entries(STAGES).map(([k, s]) => (
            <option key={k} value={k}>
              {s.label}
            </option>
          ))}
        </select>
        {FLAGS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`pc-chip ${flags.includes(f.key) ? "on" : ""}`}
            aria-pressed={flags.includes(f.key)}
            onClick={() => toggleFlag(f.key)}
          >
            {f.label}
          </button>
        ))}
        <label className="pc-toggle">
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => updateParams({ includeInternal: e.target.checked })}
          />
          Показать наших
        </label>
        <span className="pc-count">{loading ? "Загрузка…" : `${visibleItems.length} в списке`}</span>
        <button type="button" className="pc-btn" onClick={handleExport} disabled={exporting}>
          {exporting ? "Выгрузка…" : "Выгрузить в Excel"}
        </button>
      </div>

      <PaidClientsTable
        items={visibleItems}
        loading={loading}
        error={error}
        sort={sort}
        dir={dir}
        onSort={handleSort}
        selectedId={clientId}
        onOpen={(id) => openClient(id)}
      />
      </div>

      <ClientDrawer
        userId={clientId}
        tab={tab}
        onTabChange={(t) => updateParams({ tab: t })}
        onClose={closeClient}
        onOpenClient={(id) => openClient(id)}
        onChanged={load}
        notify={setToast}
      />

      <Toast text={toast} onDone={clearToast} />
    </div>
  );
};

export default PaidClientsPage;
